import {
  Box,
  Typography,
  useTheme,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";

const API = "https://ams.arcticvault.io/api/schools";

const Onboarding = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchSchools = () => {
    setLoading(true);
    fetch(API)
      .then((res) => res.json())
      .then((data) => {
        setSchools(data);
        setLoading(false);
      })
      .catch(() => {
        setSnackbar({ open: true, message: "Failed to load onboarding data.", severity: "error" });
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  const handleCopy = (link) => {
    navigator.clipboard.writeText(link);
    setSnackbar({ open: true, message: "Link copied to clipboard!", severity: "success" });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/${deleteTarget.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      setSchools((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      setSnackbar({ open: true, message: `${deleteTarget.school_name} removed.`, severity: "info" });
      setDeleteTarget(null);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateLink = async (row) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/${row.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Pending" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSchools((prev) => prev.map((s) => (s.id === row.id ? data : s)));
      setSnackbar({ open: true, message: "Status reset to Pending.", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const statusConfig = {
    Active: {
      color: colors.greenAccent[400],
      bg: "#0f2922",
      icon: <CheckCircleOutlinedIcon sx={{ fontSize: "14px" }} />,
      label: "Completed",
    },
    Pending: {
      color: "#f0a500",
      bg: "#2a1f00",
      icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: "14px" }} />,
      label: "Pending",
    },
    Suspended: {
      color: colors.redAccent[400],
      bg: "#2c100f",
      icon: <LinkOffOutlinedIcon sx={{ fontSize: "14px" }} />,
      label: "Suspended",
    },
  };

  const columns = [
    {
      field: "school_name",
      headerName: "School",
      flex: 1.2,
      renderCell: ({ row }) => (
        <Typography fontWeight="bold" color={colors.grey[100]}>{row.school_name}</Typography>
      ),
    },
    { field: "plan", headerName: "Plan", flex: 0.7 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: ({ row }) => {
        const cfg = statusConfig[row.status] || statusConfig["Pending"];
        return (
          <Chip
            icon={cfg.icon}
            label={cfg.label}
            size="small"
            sx={{
              backgroundColor: cfg.bg,
              color: cfg.color,
              fontWeight: "bold",
              fontSize: "11px",
              "& .MuiChip-icon": { color: cfg.color },
            }}
          />
        );
      },
    },
    {
      field: "created_at",
      headerName: "Created",
      flex: 0.8,
      renderCell: ({ row }) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      field: "onboarding_link",
      headerName: "Onboarding Link",
      flex: 2,
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          color={colors.blueAccent[300]}
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}
        >
          {row.onboarding_link || "—"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" gap="4px">
          <Tooltip title="Copy Link">
            <span>
              <IconButton
                onClick={() => handleCopy(row.onboarding_link)}
                disabled={!row.onboarding_link}
                sx={{ color: colors.blueAccent[300] }}
                size="small"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reset to Pending">
            <span>
              <IconButton
                onClick={() => handleRegenerateLink(row)}
                disabled={actionLoading}
                sx={{ color: "#f0a500" }}
                size="small"
              >
                <RefreshOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete School">
            <span>
              <IconButton
                onClick={() => setDeleteTarget(row)}
                disabled={actionLoading}
                sx={{ color: colors.redAccent[400] }}
                size="small"
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="ONBOARDING LINKS" subtitle="Track and manage all school onboarding links" />
        <Button
          onClick={fetchSchools}
          variant="outlined"
          startIcon={<RefreshOutlinedIcon />}
          sx={{ borderColor: colors.blueAccent[400], color: colors.blueAccent[300] }}
        >
          Refresh
        </Button>
      </Box>

      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="16px" mt="20px" mb="24px">
        {[
          { label: "Total Schools", value: schools.length, color: colors.blueAccent[300] },
          { label: "Pending Onboarding", value: schools.filter((s) => s.status === "Pending").length, color: "#f0a500" },
          { label: "Active", value: schools.filter((s) => s.status === "Active").length, color: colors.greenAccent[400] },
        ].map((stat) => (
          <Box key={stat.label} p="16px" borderRadius="8px" sx={{ backgroundColor: colors.primary[400] }}>
            <Typography variant="h3" fontWeight="bold" color={stat.color}>{stat.value}</Typography>
            <Typography variant="h6" color={colors.grey[300]}>{stat.label}</Typography>
          </Box>
        ))}
      </Box>

      <Box
        height="55vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
        }}
      >
        <DataGrid rows={schools} columns={columns} loading={loading} getRowId={(row) => row.id} />
      </Box>

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { backgroundColor: colors.primary[400], color: colors.grey[100] } }}
      >
        <DialogTitle>Delete {deleteTarget?.school_name}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: colors.grey[300] }}>
            This will permanently remove this school from the platform. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: colors.grey[300] }}>Cancel</Button>
          <Button onClick={handleDelete} disabled={actionLoading} sx={{ color: colors.redAccent[400], fontWeight: "bold" }}>
            {actionLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Onboarding;
